import React from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

/**
 * Hours on a ticket, as computed by the API — shared by the field editor
 * (FieldTicketsScreen) and the ops editor (NewTicketScreen) so both views read
 * identically.
 *
 * Never a client calculation: the server recomputes total_hours from
 * workDate/endDate/startTime/endTime on every write, so a second opinion here
 * would only drift from the number that ends up on the invoice.
 *
 * `hours` is null whenever the server has no answer. That includes the
 * contradictory case — an explicit finish date equal to the arrival date with
 * a finish time earlier than the arrival time — which is shown as a warning
 * rather than as 0, blank, or NaN.
 */
export default function TicketHoursReadout({
  hours,
  contradictory,
  dirty,
}: {
  hours: string | null;
  contradictory: boolean;
  dirty: boolean;
}) {
  if (contradictory) {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2">
        <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-red-400">
          <p className="font-semibold">Finish is before arrival on the same date.</p>
          <p className="mt-0.5 text-red-400/80">
            Hours can't be worked out from these times. If this was a night run, set the
            Finish Date to the next day.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg bg-fieldiq-dark border border-fieldiq-border px-3 py-2">
      <span className="text-xs text-fieldiq-muted flex items-center gap-1">
        <Clock size={12} /> Hours
      </span>
      {dirty ? (
        <span className="text-xs text-fieldiq-muted">Recalculated when you save</span>
      ) : hours ? (
        <span className="text-sm font-semibold text-white">{hours}</span>
      ) : (
        <span className="text-xs text-fieldiq-muted">Not calculated yet</span>
      )}
    </div>
  );
}
